// tariffServiceFeeMatrixUtils.js (ESB-641)
//
// Row assembly for the tariff and service fee matrix on CP and CC, analog to
// the reloaded tariff matrix. Each entry becomes one row with a joined value
// string. The exact column layout for mixed types is provisional until the
// decision with Christian, the row shape here stays independent of it.

import { serviceFeePriceDisplay } from '@/utils/serviceFeeViewUtils.js';

export const MATRIX_LEVELS = Object.freeze({
  CP: 'CP',
  CC: 'CC',
  C: 'C',
});

// tariff/fraction pair to a display value like "0,08933", dual tariffs as
// "primary/secondary", with the unit appended
export function tariffValueDisplay(tariff) {
  const format = (value, fraction) => {
    if (value === undefined || value === null) return '';
    const decimals = Math.min(Math.max(fraction ?? 0, 0), 6);
    let str = (value * Math.pow(10, -decimals)).toFixed(decimals);
    if (str.includes('.'))
      str = str.replace(/0+$/, '').replace(/\.$/, '');
    return str.replace('.', ',');
  };
  let display = format(tariff.tariff, tariff.fraction);
  if (tariff.secondaryTariff !== undefined && tariff.secondaryTariff !== null)
    display += '/' + format(tariff.secondaryTariff, tariff.secondaryFraction ?? tariff.fraction);
  return tariff.unit ? display + ' ' + tariff.unit : display;
}

function tariffRows(subject, level, name, listKey, entries) {
  return (entries || []).map(entry => ({
    level,
    source: name,
    entryKind: 'tariff',
    tariffList: listKey,
    tariffType: entry.type,
    energyDirection: entry.energyDirection || null,
    dateStart: entry.dateStart || null,
    dateEnd: entry.dateEnd || null,
    note: entry.note || '',
    value: tariffValueDisplay(entry),
  }));
}

function serviceFeeRows(level, name, fees) {
  return (fees || []).map(fee => ({
    level,
    source: name,
    entryKind: 'serviceFee',
    tariffList: null,
    tariffType: null,
    billingVariant: fee.billingVariant || null,
    energyDirection: null,
    dateStart: fee.dateStart || null,
    dateEnd: fee.dateEnd || null,
    note: fee.note || '',
    value: serviceFeePriceDisplay(fee),
  }));
}

function subjectRows(subject, level, name) {
  return [
    ...serviceFeeRows(level, name, subject.serviceFees),
    ...tariffRows(subject, level, name, 'fees', subject.fees),
    ...tariffRows(subject, level, name, 'tariffConsumers', subject.tariffConsumers),
    ...tariffRows(subject, level, name, 'tariffProducers', subject.tariffProducers),
  ];
}

function sortRows(rows) {
  const levelOrder = { CP: 0, CC: 1, C: 2 };
  return rows.sort((a, b) => {
    const byLevel = levelOrder[a.level] - levelOrder[b.level];
    if (byLevel !== 0) return byLevel;
    const bySource = String(a.source).localeCompare(String(b.source));
    if (bySource !== 0) return bySource;
    return new Date(b.dateStart || 0) - new Date(a.dateStart || 0);
  });
}

// CP matrix: the fees of the provider itself and the entries of all assigned
// companies (a CP carries only service fees, no tariffs)
export function matrixRowsForProvider(provider, assignedCompanies) {
  if (!provider) return [];
  const rows = serviceFeeRows(MATRIX_LEVELS.CP, provider.nameFriendly || provider.name, provider.serviceFees);
  for (const company of assignedCompanies || [])
    rows.push(...subjectRows(company, MATRIX_LEVELS.CC, company.name || company.ecn));
  return sortRows(rows);
}

// CC matrix: the assigned provider when there is one (a CC may have none),
// the company itself and all communities below it. The provider comes either
// populated on the company or resolved by the caller from the provider store.
export function matrixRowsForCompany(company, communities, resolvedProvider) {
  if (!company) return [];
  const rows = [];
  const populated = company.communityProvider;
  const provider = (populated && typeof populated === 'object' && populated._id)
    ? populated
    : resolvedProvider;
  if (provider)
    rows.push(...serviceFeeRows(MATRIX_LEVELS.CP, provider.nameFriendly || provider.name, provider.serviceFees));
  rows.push(...subjectRows(company, MATRIX_LEVELS.CC, company.name || company.ecn));
  for (const community of communities || [])
    rows.push(...subjectRows(community, MATRIX_LEVELS.C, community.name || community.ecid));
  return sortRows(rows);
}
